import { useState, useEffect } from "react";
import { sqlReadRaw as sqlRead } from "../../../utils/sqlClient";
import { generateCircuitPlan, DEFAULT_PANEL_NAME } from "../../../utils/electricalCircuitEngine";
import { phaseOptionsFor, panelLoadSummary, resolveDemandFactor } from "../../../utils/panelLoads";

const LOAD_CLASSES = ["receptacle", "lighting", "motor", "equipment", "hvac", "spare"];
const th = { textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text3)", padding: "6px 8px", borderBottom: "1px solid var(--cs-border)" };
const td = { padding: "4px 8px", borderBottom: "1px solid var(--cs-border)", fontSize: 12 };
const inputSm = { width: "100%", fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4 };

export default function PanelsCircuitsPanel({
  activeRevId, isLocked, payloadOf, rooms, criteria, equipment, connections,
  panels, circuits, assemblies, formulas, settings, onReload,
  systemsCtx = {}, // roomSystemsEngine.js context — lighting fixture count/VA from Basis of Design (see ElectricalEstimate.jsx)
}) {
  const [selectedPanelId, setSelectedPanelId] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [newPanelName, setNewPanelName] = useState("");
  const [addingPanel, setAddingPanel] = useState(false);

  useEffect(() => {
    if (!selectedPanelId && panels.length) setSelectedPanelId(panels[0].PanelID);
  }, [panels, selectedPanelId]);

  useEffect(() => {
    setRows(circuits.filter(c => c.PanelID === selectedPanelId).map(c => ({ ...c })));
  }, [circuits, selectedPanelId]);

  const roomsById = Object.fromEntries(rooms.map(r => [r.RoomID, r]));
  const formulasById = new Map(formulas.map(f => [f.FormulaID, f]));
  // Demand-factor resolution context (panelLoads.js resolveDemandFactor):
  // project design-basis criteria + CleanSpace standard tunables.
  const demandCtx = { criteria, tunables: systemsCtx.tunables };

  function updateRow(i, patch) {
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  }
  function addRow() {
    setRows(prev => [...prev, {
      CircuitID: null, PanelID: selectedPanelId, CircuitNumber: (prev.length ? Math.max(...prev.map(r => r.CircuitNumber || 0)) : 0) + 1,
      Poles: 1, TripAmps: 20, CircuitDesc: "", LoadClass: "receptacle", ConnectedVA: 0, Phase: null, DemandFactor: null,
      ConduitConductors: "", RoomID: null, AssemblyID: null, RouteLF: null, Notes: "",
    }]);
  }
  function deleteRow(i) {
    setRows(prev => prev.filter((_, idx) => idx !== i));
  }

  async function saveCircuits() {
    if (isLocked || !selectedPanelId) return;
    setSaving(true);
    try {
      const payload = payloadOf({
        panel_id: selectedPanelId,
        circuits: rows.map(r => ({
          circuit_number: r.CircuitNumber, poles: r.Poles, trip_amps: r.TripAmps || null,
          circuit_desc: r.CircuitDesc || null, load_class: r.LoadClass, connected_va: r.ConnectedVA || 0,
          conduit_conductors: r.ConduitConductors || null, connection_id: r.ConnectionID || null,
          room_id: r.RoomID || null, assembly_id: r.AssemblyID || null, route_lf: r.RouteLF ?? null,
          notes: r.Notes || null,
          phase: r.Phase || null, // explicit phase assignment (P1 Phase D-2) — never derived from the number
          demand_factor: r.DemandFactor ?? null, // estimator override only (P1 Phase D-3); blank = project basis / CleanSpace standard
        })),
      });
      await sqlRead(`EXEC sp_ReplaceEstimateCircuits @payload = '${payload}'`);
      onReload();
    } catch (e) { alert("Error saving circuits: " + e.message); }
    finally { setSaving(false); }
  }

  async function addPanel() {
    if (isLocked || !newPanelName.trim()) return;
    try {
      const payload = payloadOf({ revision_id: activeRevId, panel_name: newPanelName.trim() });
      const res = await sqlRead(`EXEC sp_SaveEstimatePanel @payload = '${payload}'`);
      const newId = (res?.Table1 || [])[0]?.PanelID;
      setNewPanelName(""); setAddingPanel(false);
      onReload();
      if (newId) setSelectedPanelId(+newId);
    } catch (e) { alert("Error adding panel: " + e.message); }
  }

  async function deletePanel(panelId) {
    if (isLocked) return;
    if (!window.confirm("Delete this panel and all its circuits?")) return;
    try {
      const payload = payloadOf({ revision_id: activeRevId, panel_id: panelId, is_delete: true });
      await sqlRead(`EXEC sp_SaveEstimatePanel @payload = '${payload}'`);
      setSelectedPanelId(null);
      onReload();
    } catch (e) { alert("Error deleting panel: " + e.message); }
  }

  async function regenerateCircuits() {
    if (isLocked || !settings) return;
    if (!window.confirm(
      "This regenerates circuits from the room takeoff for every affected panel — any circuits already " +
      "on those panels will be replaced. Continue?"
    )) return;
    setRegenerating(true);
    try {
      let currentPanels = panels;
      let plan = generateCircuitPlan({ rooms, panels: currentPanels, criteria, equipment, connections, settings, formulasById, systemsCtx });

      if (plan.needsDefaultPanel) {
        const centroid = plan.defaultPanelCentroid;
        const payload = payloadOf({
          revision_id: activeRevId, panel_name: DEFAULT_PANEL_NAME,
          canvas_x: centroid?.x ?? null, canvas_y: centroid?.y ?? null,
        });
        const res = await sqlRead(`EXEC sp_SaveEstimatePanel @payload = '${payload}'`);
        const newPanelId = +(res?.Table1 || [])[0]?.PanelID;
        currentPanels = [...currentPanels, { PanelID: newPanelId, PanelName: DEFAULT_PANEL_NAME, CanvasX: centroid?.x ?? null, CanvasY: centroid?.y ?? null }];
        plan = generateCircuitPlan({ rooms, panels: currentPanels, criteria, equipment, connections, settings, formulasById, systemsCtx });
      }

      const panelIds = Object.keys(plan.circuitsByPanelId);
      if (!panelIds.length) { alert("Nothing to generate — add rooms with device counts in Room Takeoff first."); return; }

      for (const panelId of panelIds) {
        const payload = payloadOf({ panel_id: +panelId, circuits: plan.circuitsByPanelId[panelId] });
        await sqlRead(`EXEC sp_ReplaceEstimateCircuits @payload = '${payload}'`);
      }
      onReload();
    } catch (e) { alert("Error regenerating circuits: " + e.message); }
    finally { setRegenerating(false); }
  }

  return (
    <div style={{ display: "flex", gap: 20 }}>
      {/* Panel list */}
      <div style={{ width: 220, flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text2)" }}>Panels</div>
          {!isLocked && (
            <button onClick={() => setAddingPanel(v => !v)} style={{ fontSize: 11, color: "var(--cs-blue)", background: "none", border: "none", cursor: "pointer" }}>+ Add</button>
          )}
        </div>
        {addingPanel && (
          <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
            <input value={newPanelName} onChange={e => setNewPanelName(e.target.value)} placeholder="Panel name" style={inputSm} onKeyDown={e => e.key === "Enter" && addPanel()} />
            <button onClick={addPanel} style={{ fontSize: 11 }}>Save</button>
          </div>
        )}
        {panels.map(p => (
          <div
            key={p.PanelID}
            onClick={() => setSelectedPanelId(p.PanelID)}
            style={{
              padding: "8px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12, marginBottom: 4,
              background: selectedPanelId === p.PanelID ? "var(--cs-blue-lt)" : "var(--surface)",
              border: "1px solid var(--cs-border)",
              color: selectedPanelId === p.PanelID ? "var(--cs-blue)" : "var(--text)",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}
          >
            <span>{p.PanelName} <span style={{ color: "var(--text3)" }}>({circuits.filter(c => c.PanelID === p.PanelID).length})</span></span>
            {!isLocked && <span onClick={(e) => { e.stopPropagation(); deletePanel(p.PanelID); }} style={{ color: "var(--text3)" }}>×</span>}
          </div>
        ))}
        {!panels.length && <div style={{ fontSize: 12, color: "var(--text3)" }}>No panels yet.</div>}

        {!isLocked && (
          <button
            onClick={regenerateCircuits}
            disabled={regenerating || !settings}
            style={{ marginTop: 16, width: "100%", fontSize: 12, fontWeight: 600, padding: "8px 10px", borderRadius: 6, border: "none", background: "var(--cs-blue)", color: "#fff", cursor: "pointer", opacity: regenerating ? 0.6 : 1 }}
          >
            {regenerating ? "Generating…" : "⚡ Regenerate Circuits"}
          </button>
        )}
      </div>

      {/* Circuit grid */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!selectedPanelId ? (
          <div style={{ fontSize: 13, color: "var(--text3)" }}>Select or add a panel, or click Regenerate Circuits to build a starting panel from the room takeoff.</div>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>
                {panels.find(p => p.PanelID === selectedPanelId)?.PanelName}
                {(() => {
                  // Per-phase connected load from the rows' EXPLICIT phases (unsaved edits included) — panelLoads.js.
                  const panel = panels.find(p => p.PanelID === selectedPanelId);
                  if (!panel) return null;
                  const load = panelLoadSummary(panel, rows.map(r => ({ ...r, PanelID: selectedPanelId })), demandCtx);
                  const kva = (v) => (v / 1000).toLocaleString("en-US", { maximumFractionDigits: 1 });
                  return (
                    <span style={{ marginLeft: 10, fontSize: 11.5, fontWeight: 500, color: "var(--text3)" }} title={`Connected load by explicit phase assignment (a multi-pole circuit's VA split equally across its phases). Demand = Σ connected × each circuit's demand factor — ${load.demandBasis.join("; ") || "no loads"}. Not code-compliant service sizing.`}>
                      {kva(load.connectedVA)} kVA connected · {kva(load.demandVA)} kVA demand · A {kva(load.phases.byPhase.A)} · B {kva(load.phases.byPhase.B)}{+panel.Phases !== 1 ? ` · C ${kva(load.phases.byPhase.C)}` : ""} · imbalance {load.phases.imbalancePct.toFixed(0)}%
                      {load.phases.unassignedCount ? <span style={{ color: "#b45309" }}> · {load.phases.unassignedCount} unassigned</span> : null}
                    </span>
                  );
                })()}
              </div>
              {!isLocked && (
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={addRow} style={{ fontSize: 12 }}>+ Circuit</button>
                  <button onClick={saveCircuits} disabled={saving} style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}>{saving ? "Saving…" : "Save Circuits"}</button>
                </div>
              )}
            </div>
            <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>#</th>
                    <th style={th}>Load Class</th>
                    <th style={th}>Description</th>
                    <th style={th}>Room</th>
                    <th style={th}>VA</th>
                    <th style={th} title="Explicit phase assignment — proposed at generation by least-loaded balancing, editable here, never derived from the circuit number">Phase</th>
                    <th style={th} title="Demand factor: blank = resolved from the project design basis (Design criteria ▸ Electrical) or the CleanSpace standard (Basis of Design ▸ Electrical); type a value to override this circuit. Demand VA = connected × factor. Not code-compliant sizing.">DF</th>
                    <th style={th}>Poles</th>
                    <th style={th}>Trip A</th>
                    <th style={th}>Conduit/Conductors</th>
                    <th style={th}>Assembly</th>
                    <th style={th}>Route LF</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.CircuitID ?? `new-${i}`}>
                      <td style={td}>{r.CircuitNumber}</td>
                      <td style={td}>
                        <select disabled={isLocked} value={r.LoadClass} onChange={e => updateRow(i, { LoadClass: e.target.value })} style={inputSm}>
                          {LOAD_CLASSES.map(lc => <option key={lc} value={lc}>{lc}</option>)}
                        </select>
                      </td>
                      <td style={td}><input disabled={isLocked} value={r.CircuitDesc || ""} onChange={e => updateRow(i, { CircuitDesc: e.target.value })} style={inputSm} /></td>
                      <td style={td}>{roomsById[r.RoomID]?.RoomName || "—"}</td>
                      <td style={td}><input disabled={isLocked} type="number" value={r.ConnectedVA ?? ""} onChange={e => updateRow(i, { ConnectedVA: +e.target.value })} style={{ ...inputSm, width: 70 }} /></td>
                      <td style={td}>
                        <select disabled={isLocked} value={r.Phase || ""} onChange={e => updateRow(i, { Phase: e.target.value || null })} style={{ ...inputSm, width: 64 }}>
                          <option value="">—</option>
                          {phaseOptionsFor(panels.find(p => p.PanelID === selectedPanelId)).map(ph => <option key={ph} value={ph}>{ph}</option>)}
                        </select>
                      </td>
                      <td style={td}>
                        {(() => {
                          const resolved = resolveDemandFactor({ ...r, DemandFactor: null }, demandCtx);
                          return (
                            <input disabled={isLocked} type="number" min={0.05} max={1.5} step="0.05"
                              value={r.DemandFactor ?? ""}
                              placeholder={`${resolved.factor} (${resolved.sourceLabel})`}
                              title={r.DemandFactor != null ? "Estimator override — clear to fall back" : `Resolved: ${resolved.factor} from ${resolved.sourceLabel}`}
                              onChange={e => updateRow(i, { DemandFactor: e.target.value === "" ? null : +e.target.value })}
                              style={{ ...inputSm, width: 62, fontStyle: r.DemandFactor != null ? "normal" : "italic" }} />
                          );
                        })()}
                      </td>
                      <td style={td}><input disabled={isLocked} type="number" value={r.Poles ?? 1} onChange={e => updateRow(i, { Poles: +e.target.value })} style={{ ...inputSm, width: 50 }} /></td>
                      <td style={td}><input disabled={isLocked} type="number" value={r.TripAmps ?? ""} onChange={e => updateRow(i, { TripAmps: +e.target.value })} style={{ ...inputSm, width: 55 }} /></td>
                      <td style={td}><input disabled={isLocked} value={r.ConduitConductors || ""} onChange={e => updateRow(i, { ConduitConductors: e.target.value })} style={{ ...inputSm, minWidth: 140 }} /></td>
                      <td style={td}>
                        <select disabled={isLocked} value={r.AssemblyID || ""} onChange={e => updateRow(i, { AssemblyID: e.target.value ? +e.target.value : null })} style={{ ...inputSm, minWidth: 130 }}>
                          <option value="">—</option>
                          {assemblies.map(a => <option key={a.AssemblyID} value={a.AssemblyID}>{a.Code}</option>)}
                        </select>
                      </td>
                      <td style={td}><input disabled={isLocked} type="number" value={r.RouteLF ?? ""} onChange={e => updateRow(i, { RouteLF: e.target.value === "" ? null : +e.target.value })} style={{ ...inputSm, width: 70 }} /></td>
                      <td style={td}>{!isLocked && <span onClick={() => deleteRow(i)} style={{ cursor: "pointer", color: "var(--text3)" }}>×</span>}</td>
                    </tr>
                  ))}
                  {!rows.length && (
                    <tr><td style={td} colSpan={11}>No circuits on this panel yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
